// OpenAI integration for journal analysis, summaries, and GRIPS chat
// GRIPS uses secure Firebase Function - API key is never exposed to client

// Legacy - still used for journal analysis & daily summary (move to backend when ready)
const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

interface JournalAnalysisRequest {
  responses: Array<{
    question: string;
    answer: string | number;
  }>;
  mood: number;
  timeOfDay: 'morning' | 'evening';
  userType: 'Firefighter' | 'Police' | 'Other';
}

interface JournalAnalysisResponse {
  summary: string;
  encouragement: string;
  insights: string[];
  riskFlags: string[];
  recommendations: string[];
}

export type ChatTurn = { role: 'user' | 'assistant'; content: string };

/**
 * First greeting for GRIPS.
 * Use showPrivacyLine = true only on the very first render of a new session.
 * Do NOT repeat the privacy line after the first message.
 */
export const getGRIPSGreeting = (showPrivacyLine: boolean = true): string => {
  const base =
    "How’s it going? I’m GRIPS. Let’s check in real quick. Is anything challenging going on right now?";
  const privacy =
    "(You can just talk naturally—voice-to-text is fine. This is a private, judgment-free space.)";
  return showPrivacyLine ? `${base} ${privacy}` : base;
};


/**
 * GRIPS chat completion with conversation history.
 * Calls published Agent workflow via secure Firebase Function.
 */
export const getGRIPSResponse = async (
  userMessage: string,
  conversationHistory: ChatTurn[] = [],
  userName?: string,
  responderType?: string
): Promise<string> => {
  try {
    // Trim history to last 6 turns for faster responses
    const sanitizedHistory = conversationHistory
      .filter(
        (turn): turn is ChatTurn =>
          !!turn &&
          (turn.role === 'user' || turn.role === 'assistant') &&
          typeof turn.content === 'string'
      )
      .map((turn) => ({
        role: turn.role,
        content: turn.content,
      }))
      .slice(-6);

    const response = await fetch('/api/gripsRespond', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: userMessage,
        history: sanitizedHistory,
        userName: userName || '',
        responderType: responderType || '',
      }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || !data?.success || typeof data.reply !== 'string') {
      console.error('GRIPS Agent error:', data);
      throw new Error(data?.error || 'Failed to get GRIPS response');
    }

    return data.reply;
  } catch (error) {
    console.error('Error getting GRIPS response:', error);
    return "I'm sorry, I'm having trouble connecting right now. If you want to share what's going on, I'm here to listen.";
  }
};

/** =========================
 *  Journal Analysis (GRIPS-aligned voice, JSON output)
 *  ========================= */
export const analyzeJournalEntry = async (
  data: JournalAnalysisRequest
): Promise<JournalAnalysisResponse> => {
  try {
    const prompt = createAnalysisPrompt(data);

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4-mini', // Fast, cost-effective model
        messages: [
          {
            role: 'system',
            content: `
You are GRIPS Journal — same voice and culture as GRIPS.
Read their wellness check-in and return a short, supportive JSON summary.

Rules:
- Same first responder cultural fluency (fire/EMS/LE/dispatch/corrections).
- Non-clinical, non-diagnostic, no medical or legal advice.
- Tone: grounded, appreciative, human, concise, strengths-based.
- Validate effort, normalize stress, highlight resilience.
- Be conservative and calm when flagging risk.
- Output MUST be valid JSON ONLY in the requested structure. No extra text.
            `.trim(),
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        max_tokens: 800,
        temperature: 0.65,
      }),
    });

    if (!response.ok) {
      throw new Error('OpenAI API request failed');
    }

    const result = await response.json();
    const aiResponse = result.choices?.[0]?.message?.content ?? '';
    return parseAIResponse(aiResponse);
  } catch (error) {
    console.error('Error analyzing journal entry:', error);

    return {
      summary:
        'Thank you for taking time for your wellness check-in. Your commitment to your own mental health matters.',
      encouragement:
        'You carry a lot and still show up for others. That effort counts, even on the tough days.',
      insights: [
        'Regular check-ins help you notice patterns before they pile up.',
        'Being honest about how you are doing is a strength, not a weakness.',
      ],
      riskFlags: [],
      recommendations: [
        'Keep using quick check-ins to stay aware of stress levels.',
        'Reach out to a peer, chaplain, or clinician if things start to feel heavier or stuck.',
      ],
    };
  }
};

const createAnalysisPrompt = (data: JournalAnalysisRequest): string => {
  const timeContext =
    data.timeOfDay === 'morning'
      ? 'starting their shift/day'
      : 'ending their shift/day';

  return `
Analyze this ${data.timeOfDay} wellness check-in from a ${data.userType} first responder who is ${timeContext}.

Overall Mood Rating: ${data.mood}/10

Responses:
${data.responses
  .map((r) => `Q: ${r.question}\nA: ${r.answer}`)
  .join('\n\n')}

Return ONLY valid JSON in this exact structure:
{
  "summary": "2-3 sentences. Brief, human, supportive reflection of where they seem to be at.",
  "encouragement": "2-3 sentences. Normalize their experience, thank them for what they carry, and encourage continued awareness.",
  "insights": [
    "2-3 short, specific observations that might help them understand patterns or stress points."
  ],
  "riskFlags": [
    "List any clearly concerning signals (e.g. mentions of hopelessness, self-harm, feeling like a burden, unsafe substance use, drastic changes). Keep this list conservative and non-dramatic."
  ],
  "recommendations": [
    "2-3 concrete, realistic next steps: simple coping tools, peer support, rest, movement, talking with someone they trust, or seeking professional support if needed."
  ]
}
`.trim();
};

const parseAIResponse = (aiResponse: string): JournalAnalysisResponse => {
  try {
    const parsed = JSON.parse(aiResponse);

    return {
      summary:
        typeof parsed.summary === 'string'
          ? parsed.summary
          : 'Thank you for your wellness check-in.',
      encouragement:
        typeof parsed.encouragement === 'string'
          ? parsed.encouragement
          : 'Your commitment to checking in with yourself is a real strength.',
      insights: Array.isArray(parsed.insights) ? parsed.insights : [],
      riskFlags: Array.isArray(parsed.riskFlags) ? parsed.riskFlags : [],
      recommendations: Array.isArray(parsed.recommendations)
        ? parsed.recommendations
        : [],
    };
  } catch (error) {
    console.error('Error parsing AI response, using fallback:', error);

    return {
      summary:
        'Thank you for taking time for your wellness check-in today.',
      encouragement:
        'You are carrying a lot and still making space to reflect. That matters more than you think.',
      insights: [
        'These small moments of honesty help catch stress before it boils over.',
        'Noticing how you feel across shifts is part of taking care of yourself and your crew.',
      ],
      riskFlags: [],
      recommendations: [
        'Keep up with brief daily check-ins.',
        'If things feel heavier or stuck, consider looping in a trusted peer, leader, or clinician.',
      ],
    };
  }
};

/** =========================
 *  Daily Summary (GRIPS-style supportive recap)
 *  ========================= */
export const generateDailySummary = async (
  morningEntry?: { overallMood?: number; [key: string]: unknown },
  eveningEntry?: { overallMood?: number; [key: string]: unknown },
  userType: string = 'First Responder'
): Promise<string> => {
  try {
    const prompt = `
Create a short daily reflection for a ${userType} based on their wellness check-ins.

${morningEntry
  ? `Morning Check-in - Mood: ${morningEntry.overallMood}/10`
  : 'No morning check-in completed.'}
${eveningEntry
  ? `Evening Check-in - Mood: ${eveningEntry.overallMood}/10`
  : 'No evening check-in completed.'}

Requirements:
- Same voice and culture as GRIPS (peer-like, grounded, non-clinical).
- 3-4 sentences max.
- Acknowledge what they are carrying without drama.
- Highlight any small wins or patterns (even "you checked in, that counts").
- Offer one gentle nudge for ongoing care (rest, connection, simple coping).
- End with a strengths-based note that leaves the door open.
`.trim();

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content:
              'You are GRIPS Daily — a supportive, peer-voice assistant for first responders. Keep it brief, real, and encouraging.',
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        max_tokens: 300,
        temperature: 0.65,
      }),
    });

    if (!response.ok) {
      throw new Error('OpenAI API request failed');
    }

    const result = await response.json();
    return (
      result.choices?.[0]?.message?.content ??
      'Thanks for checking in today. Even taking a minute to notice how you are doing is a solid move, and it all adds up.'
    );
  } catch (error) {
    console.error('Error generating daily summary:', error);
    return (
      'Thank you for prioritizing your wellness today. Every check-in is a small but real investment in your resilience, on and off shift.'
    );
  }
};
